import { useMemo } from 'react';
import {
  Autocomplete,
  FormControls,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { ValueLabelOption } from '@elementary/common-ui/types';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AlertDestinationFormData } from 'shared/types';
import { isValidEmail, extractEmailsFromText } from 'shared/utils/validators';

type Props = {
  methods: UseFormReturn<AlertDestinationFormData, any, undefined>;
  emails: ValueLabelOption[];
  autoSubmit?: boolean;
  onAutoSubmit?: () => void;
};

const EmailsAutocompleteField = ({
  methods,
  emails,
  autoSubmit,
  onAutoSubmit,
}: Props) => {
  const destination = methods.watch('destination');

  const selectedEmails = useMemo(
    () => (Array.isArray(destination) ? destination : []),
    [destination],
  );

  const availableEmails = useMemo(() => {
    const selectedEmailValues = selectedEmails.map((item) => item.value);
    return emails.filter(
      (emailOption) => !selectedEmailValues.includes(emailOption.value),
    );
  }, [emails, selectedEmails]);

  const hasInvalidEmails = (values: ValueLabelOption[]): boolean =>
    values.some((item) => !isValidEmail(item.value));

  const handlePaste = (
    event: React.ClipboardEvent<HTMLDivElement>,
    currentValue: ValueLabelOption[] | undefined,
    onChange: (value: ValueLabelOption[]) => void,
  ) => {
    const pastedText = event.clipboardData.getData('text');
    const extractedEmails = extractEmailsFromText(pastedText);

    if (extractedEmails.length > 0) {
      event.preventDefault();
      const currentValues = currentValue || [];
      const existingEmails = currentValues.map((item) => item.value);
      const newEmails = extractedEmails
        .filter((email) => !existingEmails.includes(email))
        .map((email) => ({ value: email, label: email }));

      onChange([...currentValues, ...newEmails]);
    }
  };

  return (
    <FormControls.InputWrapper style={{ flex: 1 }}>
      <Controller
        name="destination"
        control={methods.control}
        rules={{ required: requiredLabel }}
        render={({ field }) => (
          <div
            onPaste={(e) =>
              handlePaste(
                e,
                Array.isArray(field.value) ? field.value : [],
                field.onChange,
              )
            }
          >
            <Autocomplete
              value={Array.isArray(field.value) ? field.value : []}
              size="large"
              loadingText="Loading user emails..."
              placeholder="Choose or enter email addresses"
              options={availableEmails}
              freeSolo
              multiple
              disabled={!methods.watch('alertProvider')}
              valueValidator={isValidEmail}
              invalidMessage="Please enter a valid email address."
              onChange={(_, val) => {
                const normalizedVal = val.map(
                  (item: string | ValueLabelOption) =>
                    typeof item === 'string'
                      ? { value: item, label: item }
                      : item,
                );
                field.onChange(normalizedVal);
              }}
              onBlur={() => {
                if (autoSubmit) {
                  const currentValue = Array.isArray(field.value)
                    ? field.value
                    : [];

                  if (
                    !hasInvalidEmails(currentValue) &&
                    currentValue.length > 0
                  ) {
                    onAutoSubmit?.();
                  }
                }
              }}
            />
          </div>
        )}
      />
    </FormControls.InputWrapper>
  );
};

export default EmailsAutocompleteField;
